import { useMemo, useState } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { ProtocolMode, PublicClientApplication } from '@azure/msal-browser';
import { SessionContext } from './SessionContext';
import { InvalidSessionModal } from './InvalidSessionModal';
import type { Config } from '../config/types';

type Props = {
  children: React.ReactNode;
  config: Config;
};

export function AuthProvider(props: Props) {
  const [isShowingInvalidSession, setShowingInvalidSession] = useState(false);
  const msalInstance = useMemo(
    () =>
      new PublicClientApplication({
        auth: {
          authority: props.config.authority,
          clientId: props.config.clientId,
          redirectUri: props.config.redirectUri,
          postLogoutRedirectUri: props.config.logoutUri,
          knownAuthorities: props.config.knownAuthorities,
          protocolMode: props.config.protocolMode
            ? ProtocolMode[props.config.protocolMode]
            : undefined,
          authorityMetadata: JSON.stringify(props.config.authorityMetadata),
        },
      }),
    [props.config]
  );

  const msalLoginRequest = useMemo(
    () => ({
      scopes: props.config.scopes,
    }),
    [props.config.scopes]
  );

  function showInvalidSession() {
    setShowingInvalidSession(true);
  }

  const location = useLocation();
  const navigate = useNavigate();
  function login() {
    setShowingInvalidSession(false);
    navigate('/login', { replace: true, state: { from: location.pathname } });
  }

  function logout() {
    setShowingInvalidSession(false);
    navigate('/logout');
  }

  return (
    <SessionContext.Provider value={{ showInvalidSession, msalLoginRequest }}>
      {isShowingInvalidSession && <InvalidSessionModal login={login} logout={logout} />}
      <MsalProvider instance={msalInstance}>{props.children}</MsalProvider>
    </SessionContext.Provider>
  );
}
